import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Card from '../../ui/Card';

// Mock API function for tournament participants (users who have bets)
const fetchTournamentParticipants = async (tournamentId) => {
  // In a real app, this would be an API call
  // For now, return mock data
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return [
    {
      id: '1',
      user: {
        id: 'user1',
        firstName: 'John',
        lastName: 'Smith',
        email: 'john.smith@email.com'
      },
      totalBets: 3,
      totalAmount: 650,
      bets: [
        { golferName: 'Tiger Woods', amount: 250, tier: 'A' },
        { golferName: 'Rory McIlroy', amount: 200, tier: 'B' },
        { golferName: 'Jordan Spieth', amount: 200, tier: 'B' }
      ],
      joinedAt: '2024-01-15T10:30:00Z',
      lastActivity: '2024-01-20T14:45:00Z'
    },
    {
      id: '2',
      user: {
        id: 'user2',
        firstName: 'Sarah',
        lastName: 'Johnson',
        email: 'sarah.johnson@email.com'
      },
      totalBets: 2,
      totalAmount: 350,
      bets: [
        { golferName: 'Dustin Johnson', amount: 150, tier: 'C' },
        { golferName: 'Brooks Koepka', amount: 200, tier: 'B' }
      ],
      joinedAt: '2024-01-16T09:15:00Z',
      lastActivity: '2024-01-21T11:20:00Z'
    },
    {
      id: '3',
      user: {
        id: 'user3',
        firstName: 'Mike',
        lastName: 'Davis',
        email: 'mike.davis@email.com'
      },
      totalBets: 1,
      totalAmount: 100,
      bets: [
        { golferName: 'Scottie Scheffler', amount: 100, tier: 'D' }
      ],
      joinedAt: '2024-01-17T16:22:00Z',
      lastActivity: '2024-01-19T13:10:00Z'
    }
  ];
};

const TournamentParticipantsTab = ({ tournament, canModify }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('totalAmount'); // totalAmount, totalBets, joinedAt
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch tournament participants
  const { data: participants, isLoading: participantsLoading } = useQuery({
    queryKey: ['tournament-participants', tournament?.id],
    queryFn: () => fetchTournamentParticipants(tournament.id),
    enabled: !!tournament,
    refetchOnWindowFocus: false,
  });

  // Filter and sort participants
  const filteredParticipants = (participants || [])
    .filter(participant => {
      const fullName = `${participant.user.firstName} ${participant.user.lastName}`.toLowerCase();
      const email = participant.user.email.toLowerCase();
      const search = searchTerm.toLowerCase();
      return fullName.includes(search) || email.includes(search);
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'totalAmount':
          aVal = a.totalAmount;
          bVal = b.totalAmount;
          break;
        case 'totalBets':
          aVal = a.totalBets;
          bVal = b.totalBets;
          break;
        case 'joinedAt':
          aVal = new Date(a.joinedAt);
          bVal = new Date(b.joinedAt);
          break;
        default:
          aVal = a.totalAmount;
          bVal = b.totalAmount;
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const totalParticipants = participants?.length || 0;
  const totalBetsAmount = participants?.reduce((sum, p) => sum + p.totalAmount, 0) || 0;
  const totalBetsCount = participants?.reduce((sum, p) => sum + p.totalBets, 0) || 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-tournament-participants-tab">
      <div className="tab-header">
        <h2>Tournament Participants</h2>
        <p>View users who have placed bets in this tournament</p>
      </div>

      {/* Statistics Cards */}
      <div className="admin-participants-stats">
        <Card className="admin-stat-card">
          <div className="admin-stat-value">{totalParticipants}</div>
          <div className="admin-stat-label">Total Participants</div>
        </Card>
        <Card className="admin-stat-card">
          <div className="admin-stat-value">{formatCurrency(totalBetsAmount)}</div>
          <div className="admin-stat-label">Total Bets Amount</div>
        </Card>
        <Card className="admin-stat-card">
          <div className="admin-stat-value">{totalBetsCount}</div>
          <div className="admin-stat-label">Total Bets Count</div>
        </Card>
        <Card className="admin-stat-card">
          <div className="admin-stat-value">{totalParticipants > 0 ? formatCurrency(totalBetsAmount / totalParticipants) : '$0'}</div>
          <div className="admin-stat-label">Average per Participant</div>
        </Card>
      </div>

      {/* Controls */}
      <div className="admin-participants-controls">
        <div className="admin-search-controls">
          <Input
            type="text"
            placeholder="Search participants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-search-input"
            lightTheme={true}
          />
        </div>

        <div className="admin-sort-controls">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="admin-sort-select"
          >
            <option value="totalAmount">Total Amount</option>
            <option value="totalBets">Number of Bets</option>
            <option value="joinedAt">Join Date</option>
          </select>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="admin-sort-order-btn"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Participants List */}
      <div className="admin-participants-content">
        {participantsLoading ? (
          <div className="admin-loading-state">Loading participants...</div>
        ) : filteredParticipants.length === 0 ? (
          <Card className="admin-empty-state">
            <h3>No Participants Found</h3>
            <p>
              {searchTerm 
                ? 'No participants match your search criteria.' 
                : 'This tournament has no participants yet.'}
            </p>
          </Card>
        ) : (
          <div className="admin-participants-list">
            {filteredParticipants.map((participant) => (
              <Card key={participant.id} className="admin-participant-card">
                <div className="admin-participant-header">
                  <div className="admin-participant-info">
                    <h4>{participant.user.firstName} {participant.user.lastName}</h4>
                    <p className="admin-participant-email">{participant.user.email}</p>
                  </div>
                  <div className="admin-participant-stats-summary">
                    <div className="admin-stat-item">
                      <span className="admin-stat-number">{participant.totalBets}</span>
                      <span className="admin-stat-text">bets</span>
                    </div>
                    <div className="admin-stat-item">
                      <span className="admin-stat-number">{formatCurrency(participant.totalAmount)}</span>
                      <span className="admin-stat-text">total</span>
                    </div>
                  </div>
                </div>

                <div className="admin-participant-bets">
                  <h5>Player Bets:</h5>
                  <div className="admin-bets-list">
                    {participant.bets.map((bet, index) => (
                      <div key={index} className="admin-bet-item">
                        <span className="admin-bet-player">{bet.golferName}</span>
                        <span className={`admin-bet-tier admin-tier-${bet.tier.toLowerCase()}`}>
                          Tier {bet.tier}
                        </span>
                        <span className="admin-bet-amount">{formatCurrency(bet.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="admin-participant-timeline">
                  <div className="admin-timeline-item">
                    <span className="admin-timeline-label">Joined:</span>
                    <span className="admin-timeline-date">{formatDate(participant.joinedAt)}</span>
                  </div>
                  <div className="admin-timeline-item">
                    <span className="admin-timeline-label">Last Activity:</span>
                    <span className="admin-timeline-date">{formatDate(participant.lastActivity)}</span>
                  </div>
                </div>

                {canModify && (
                  <div className="admin-participant-actions">
                    <Button size="small" variant="secondary">
                      View Details
                    </Button>
                    <Button size="small" variant="secondary">
                      Contact User
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentParticipantsTab;